import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";

const BRAND_LINKS = [
  { label: "Seafood Restaurant", path: "/amrut-seafood", testId: "nav-seafood-link" },
  { label: "Amrut Aura", path: "/amrut-aura", testId: "nav-aura-link" },
  { label: "Amrut Inn", path: "/amrut-inn", testId: "nav-inn-link" },
  { label: "Amrut Jewellers", path: "/amrut-jewellers", testId: "nav-jewellers-link" },
  { label: "Amrut Delight", path: "/amrut-delight", testId: "nav-bakery-link" },
];

const ALL_MOBILE_LINKS = [
  { label: "Home", path: "/", testId: "nav-home-link" },
  { label: "About", path: "/about", testId: "nav-about-link" },
  ...BRAND_LINKS,
];

const WHATSAPP_URL = "https://wa.me/919845201215?text=Hello%20Amrut%20Group";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setBrandsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setBrandsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setBrandsOpen(false), 200);
  };

  const isBrandPage = BRAND_LINKS.some((l) => l.path === location.pathname);

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "nav-glass-scrolled" : "nav-glass"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link
              to="/"
              data-testid="nav-logo"
              className="font-serif text-xl md:text-2xl tracking-tight text-charcoal font-light"
            >
              Amrut Group
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-10">
              <Link
                to="/"
                data-testid="nav-home-link"
                className={`overline-text link-underline transition-opacity duration-300 ${
                  location.pathname === "/" ? "text-charcoal opacity-100" : "text-charcoal/70 hover:opacity-100"
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                data-testid="nav-about-link"
                className={`overline-text link-underline transition-opacity duration-300 ${
                  location.pathname === "/about" ? "text-charcoal opacity-100" : "text-charcoal/70 hover:opacity-100"
                }`}
              >
                About
              </Link>

              {/* Brands Dropdown */}
              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  data-testid="nav-brands-dropdown"
                  className={`overline-text flex items-center gap-1.5 transition-opacity duration-300 ${
                    isBrandPage ? "text-charcoal opacity-100" : "text-charcoal/70 hover:opacity-100"
                  }`}
                >
                  Experiences
                  <ChevronDown
                    size={12}
                    strokeWidth={1.5}
                    className={`transition-transform duration-300 ${brandsOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${
                    brandsOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-2"
                  }`}
                >
                  <div className="bg-ivory border border-charcoal/8 min-w-[220px] py-3">
                    {BRAND_LINKS.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={link.testId}
                        className={`block px-6 py-2.5 text-xs tracking-[0.12em] uppercase transition-all duration-200 ${
                          location.pathname === link.path
                            ? "text-charcoal bg-charcoal/5"
                            : "text-charcoal/50 hover:text-charcoal hover:bg-charcoal/3"
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop WhatsApp CTA */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-btn"
              className="hidden lg:flex items-center gap-2 px-5 py-2 bg-charcoal text-ivory text-xs tracking-[0.15em] uppercase font-medium transition-all duration-300 hover:bg-charcoal/90"
            >
              <Phone size={13} strokeWidth={1.5} />
              Contact
            </a>

            {/* Mobile Toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-charcoal"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-40 bg-ivory transition-all duration-500 lg:hidden overflow-y-auto ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col items-start justify-center min-h-full px-10 py-24 gap-6">
          {ALL_MOBILE_LINKS.map((link, i) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-${link.testId}`}
              className={`font-serif text-3xl md:text-4xl tracking-tight font-light transition-all duration-300 ${
                location.pathname === link.path
                  ? "text-charcoal"
                  : "text-charcoal/40 hover:text-charcoal"
              }`}
              style={{ transitionDelay: mobileOpen ? `${i * 60}ms` : "0ms" }}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-whatsapp-btn"
            className="mt-6 flex items-center gap-3 px-6 py-3 bg-charcoal text-ivory text-xs tracking-[0.15em] uppercase font-medium"
          >
            <Phone size={14} strokeWidth={1.5} />
            Contact Us
          </a>
        </div>
      </div>
    </>
  );
}
